import { Card, Tag, Typography, Button, Row, Col, Space } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { TranscriptionCard } from './transcription';
import { useGetCallDetails } from 'src/hooks/call';

const { Text } = Typography;

export const CallDetails = () => {
  const { callId } = useParams<{ callId: string }>();
  const navigate = useNavigate();
  const { data: call, isLoading } = useGetCallDetails({ callId: callId! });

  if (!callId) {
    navigate('/frontdesk/calls');
    return null;
  }

  const formatDuration = (durationSeconds: number | null) => {
    if (!durationSeconds) return 'N/A';
    const minutes = Math.floor(durationSeconds / 60);
    const seconds = Math.floor(durationSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatTime = (timestamp: string | null) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDate = (timestamp: string | null) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    if (status === 'ended' || status === 'completed') return 'green';
    if (status === 'failed') return 'red';
    if (status === 'in_progress' || status === 'active') return 'blue';
    return 'default';
  };

  return (
    <div className="page-wrapper care-connect-page flex flex-col gap-4 h-full">
      <div className="flex items-center gap-4 mb-4">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/frontdesk/calls')}
          size="large"
        >
          Back to Calls
        </Button>
      </div>

      {isLoading ? (
        <Card loading />
      ) : call ? (
        <div className="space-y-6">
          {/* Patient and Caller Information Row */}
          <Row gutter={24}>
            <Col xs={24} lg={12}>
              <Card
                title={
                  <div className="flex justify-between items-start">
                    <span>Patient Information</span>
                    <Space>
                      <Tag color={getStatusColor(call.status)}>
                        {call.status.toUpperCase()}
                      </Tag>
                    </Space>
                  </div>
                }
                className="h-full"
              >
                <div className="space-y-3">
                  <div>
                    <Text strong className="block text-sm">
                      Phone Number
                    </Text>
                    <Text className="text-base">
                      {call.direction === 'inbound'
                        ? call.source
                        : call.destination || 'N/A'}
                    </Text>
                  </div>

                  <div>
                    <Text strong className="block text-sm">
                      Name
                    </Text>
                    <Text className="text-base">
                      {call.patient?.first_name
                        ? `${call.patient.first_name} ${
                            call.patient?.last_name || ''
                          }`.trim()
                        : 'N/A'}
                    </Text>
                  </div>

                  <div>
                    <Text strong className="block text-sm">
                      Date of Birth
                    </Text>
                    <Text className="text-base">
                      {call.patient?.date_of_birth || 'N/A'}
                    </Text>
                  </div>

                  <div>
                    <Text strong className="block text-sm">
                      Age
                    </Text>
                    <Text className="text-base">
                      {call.patient?.age || 'N/A'}
                    </Text>
                  </div>

                  <div>
                    <Text strong className="block text-sm">
                      Gender
                    </Text>
                    <Text className="text-base">
                      {call.patient?.gender || 'N/A'}
                    </Text>
                  </div>
                </div>
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card title="Caller Information" className="h-full">
                <div className="space-y-3">
                  <div>
                    <Text strong className="block text-sm">
                      Source Number
                    </Text>
                    <Text className="text-base">{call.source || 'N/A'}</Text>
                  </div>

                  <div>
                    <Text strong className="block text-sm">
                      Destination Number
                    </Text>
                    <Text className="text-base">
                      {call.destination || 'N/A'}
                    </Text>
                  </div>

                  <div>
                    <Text strong className="block text-sm">
                      Call Direction
                    </Text>
                    <Text className="text-base">{call.direction || 'N/A'}</Text>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Call Details */}
          <Card title="Call Details">
            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} md={6}>
                <div>
                  <Text strong className="block text-sm">
                    Call Time
                  </Text>
                  <Text className="text-base">
                    {formatTime(call.start_time)}
                  </Text>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div>
                  <Text strong className="block text-sm">
                    Duration
                  </Text>
                  <Text className="text-base">
                    {formatDuration(call.duration)}
                  </Text>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div>
                  <Text strong className="block text-sm">
                    End Time
                  </Text>
                  <Text className="text-base">{formatTime(call.end_time)}</Text>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div>
                  <Text strong className="block text-sm">
                    Call Type
                  </Text>
                  <Text className="text-base">
                    {call.type.replace('_', ' ').toUpperCase()}
                  </Text>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div>
                  <Text strong className="block text-sm">
                    Priority Level
                  </Text>
                  <Text className="text-base">{call.priority}</Text>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div>
                  <Text strong className="block text-sm">
                    Disconnect Reason
                  </Text>
                  <Text className="text-base">
                    {call.disconnect_reason || 'N/A'}
                  </Text>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div>
                  <Text strong className="block text-sm">
                    Start Date
                  </Text>
                  <Text className="text-base">
                    {formatDate(call.start_time)}
                  </Text>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div>
                  <Text strong className="block text-sm">
                    End Date
                  </Text>
                  <Text className="text-base">{formatDate(call.end_time)}</Text>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div>
                  <Text strong className="block text-sm">
                    Created At
                  </Text>
                  <Text className="text-base">
                    {formatDate(call.created_at)}
                  </Text>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div>
                  <Text strong className="block text-sm">
                    Updated At
                  </Text>
                  <Text className="text-base">
                    {formatDate(call.updated_at)}
                  </Text>
                </div>
              </Col>
            </Row>
          </Card>
          {/* Call Transcript */}
          <TranscriptionCard
            encounterId={call?.encounter_id || ''}
            phase={null}
          />
        </div>
      ) : (
        <Card>
          <div className="text-center py-8">
            <Text type="secondary">Call not found</Text>
          </div>
        </Card>
      )}
    </div>
  );
};
